import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import GoogleCalendarService from '../../services/googleCalendarService';
import Button from '../common/Button';
import './Integrations.css';

const Integrations = () => {
  const { user } = useAuth();
  const [calendarStatus, setCalendarStatus] = useState({
    connected: false,
    connectedAt: null,
    hasValidTokens: false
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const loadCalendarStatus = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await GoogleCalendarService.getConnectionStatus(user.access_token);
      
      if (response.success) {
        setCalendarStatus(response.data);
      } else {
        setMessage({
          type: 'error',
          text: 'Error al cargar el estado de las integraciones'
        });
      }
    } catch (error) {
      console.error('Error loading calendar status:', error);
      setMessage({
        type: 'error',
        text: 'Error al cargar el estado de las integraciones'
      });
    } finally {
      setIsLoading(false);
    }
  }, [user.access_token]);

  // Load calendar status on component mount
  useEffect(() => {
    loadCalendarStatus();
    
    // Handle OAuth callback if present
    const callback = GoogleCalendarService.handleOAuthCallback();
    if (callback) {
      if (callback.calendarConnected) {
        setMessage({
          type: 'success',
          text: '¡Google Calendar conectado exitosamente!'
        });
        GoogleCalendarService.cleanOAuthParams();
        // Reload status after successful connection
        setTimeout(() => loadCalendarStatus(), 1000);
      } else if (callback.calendarError || callback.error) {
        setMessage({
          type: 'error',
          text: 'Error al conectar Google Calendar. Inténtalo de nuevo.'
        });
        GoogleCalendarService.cleanOAuthParams();
      }
    }
  }, [loadCalendarStatus]);

  const handleConnectCalendar = async () => {
    try {
      setIsConnecting(true);
      setMessage({ type: '', text: '' });
      
      const response = await GoogleCalendarService.getAuthUrl(user.access_token);
      
      if (response.success && response.data.authUrl) {
        // Redirect to Google OAuth
        window.location.href = response.data.authUrl + '&redirect_to=frontend';
      } else {
        setMessage({
          type: 'error',
          text: 'Error al obtener la URL de autorización'
        });
      }
    } catch (error) {
      console.error('Error connecting calendar:', error);
      setMessage({
        type: 'error',
        text: 'Error al conectar Google Calendar'
      });
    } finally {
      setIsConnecting(false);
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm('¿Estás seguro de que deseas desconectar Google Calendar? Se dejarán de crear eventos automáticamente para las citas.')) {
      return;
    }

    try {
      setIsDisconnecting(true);
      setMessage({ type: '', text: '' });
      
      const response = await GoogleCalendarService.disconnect(user.access_token);
      
      if (response.success) {
        setMessage({
          type: 'success',
          text: 'Google Calendar desconectado exitosamente'
        });
        await loadCalendarStatus();
      } else {
        setMessage({
          type: 'error',
          text: response.message || 'Error al desconectar Google Calendar'
        });
      }
    } catch (error) {
      console.error('Error disconnecting calendar:', error);
      setMessage({
        type: 'error',
        text: 'Error al desconectar Google Calendar'
      });
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleTestConnection = async () => {
    try {
      setIsTesting(true);
      setMessage({ type: '', text: '' });
      
      const response = await GoogleCalendarService.testConnection(user.access_token);
      
      if (response.success) {
        setMessage({
          type: 'success',
          text: 'Conexión con Google Calendar verificada correctamente'
        });
      } else {
        setMessage({
          type: 'error',
          text: response.message || 'Error al verificar la conexión'
        });
        // If connection test failed, reload status
        await loadCalendarStatus();
      }
    } catch (error) {
      console.error('Error testing calendar connection:', error);
      setMessage({
        type: 'error',
        text: 'Error al verificar la conexión con Google Calendar'
      });
    } finally {
      setIsTesting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Nunca';
    
    try {
      return new Date(dateString).toLocaleString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return 'Fecha inválida';
    }
  };

  if (isLoading) {
    return (
      <div className="integrations-container">
        <div className="integrations-header">
          <h1>Integraciones</h1>
          <p>Conecta servicios externos para mejorar tu flujo de trabajo</p>
        </div>
        <div className="integrations-loading">
          <div className="loading-spinner"></div>
          <p>Cargando integraciones...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="integrations-container">
      <div className="integrations-header">
        <h1>Integraciones</h1>
        <p>Conecta servicios externos para mejorar tu flujo de trabajo</p>
      </div>

      <div className="integrations-card">
        {message.text && (
          <div className={`message ${message.type}`}>
            {message.text}
          </div>
        )}

        <div className="integrations-content">
        {/* Google Calendar Integration */}
        <div className="integration-card">
          <div className="integration-header">
            <div className="integration-logo">
              <img 
                src="https://developers.google.com/calendar/images/calendar_32dp.png" 
                alt="Google Calendar" 
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'flex';
                }}
              />
              <div className="logo-fallback" style={{ display: 'none' }}>
                <i className="fas fa-calendar-alt"></i>
              </div>
            </div>
            <div className="integration-info">
              <h3>Google Calendar</h3>
              <p>Sincroniza automáticamente las citas con tu calendario de Google</p>
            </div>
            <div className={`connection-status ${calendarStatus.connected ? 'connected' : 'disconnected'}`}>
              <i className={`fas ${calendarStatus.connected ? 'fa-check-circle' : 'fa-times-circle'}`}></i>
              <span>{calendarStatus.connected ? 'Conectado' : 'Desconectado'}</span>
            </div>
          </div>

          <div className="integration-body">
            <div className="integration-features">
              <h4>Características:</h4>
              <ul>
                <li>
                  <i className="fas fa-check"></i>
                  Creación automática de eventos para nuevas citas
                </li>
                <li>
                  <i className="fas fa-check"></i>
                  Actualización automática cuando se modifican las citas
                </li>
                <li>
                  <i className="fas fa-check"></i>
                  Eliminación de eventos cuando se cancelan las citas
                </li>
                <li>
                  <i className="fas fa-check"></i>
                  Invitaciones automáticas para pacientes y doctores
                </li>
              </ul>
            </div>

            {calendarStatus.connected && (
              <div className="connection-details">
                <div className="detail-item">
                  <strong>Conectado el:</strong>
                  <span>{formatDate(calendarStatus.connectedAt)}</span>
                </div>
                <div className="detail-item">
                  <strong>Estado de tokens:</strong>
                  <span className={calendarStatus.hasValidTokens ? 'status-valid' : 'status-invalid'}>
                    {calendarStatus.hasValidTokens ? 'Válidos' : 'Expirados o inválidos'}
                  </span>
                </div>
              </div>
            )}

            <div className="integration-actions">
              {!calendarStatus.connected ? (
                <Button
                  variant="primary"
                  onClick={handleConnectCalendar}
                  loading={isConnecting}
                  icon="fas fa-link"
                >
                  {isConnecting ? 'Conectando...' : 'Conectar Google Calendar'}
                </Button>
              ) : (
                <div className="connected-actions">
                  <Button
                    variant="outline"
                    onClick={handleTestConnection}
                    loading={isTesting}
                    icon="fas fa-check-circle"
                  >
                    {isTesting ? 'Verificando...' : 'Verificar Conexión'}
                  </Button>
                  <Button
                    variant="danger"
                    onClick={handleDisconnectCalendar}
                    loading={isDisconnecting}
                    icon="fas fa-unlink"
                  >
                    {isDisconnecting ? 'Desconectando...' : 'Desconectar'}
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default Integrations;
